import {
  ItemInfo,
  ItemHoverDetail,
  ItemFocusDetail,
  Bounds,
  DiagramNodeData,
  DiagramLinkData,
  ExpandedItemDetail,
  PromotedLinkId
} from '../diagram.types';
import { useRef, useState } from 'preact/hooks';
import {
  isEqualItem,
  getNavigableInfo,
  changeExpanded,
  expCollapseButtonId,
  isPromoted,
  getChildInfo
} from '../utils/diagramUtils';
import { zoomAndCenterItemFromInfo } from '../utils/zoomUtils';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { getLinkDetailFromInfo } from '../utils/navUtils';
import { useDiagramNavigation } from './useDiagramNavigation';
import type { NavDirection } from '../utils/navUtils';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { gestureTypes } from '#hooks/PRIVATE_useVisContextMenu';
import { Keys } from '#utils/UNSAFE_keys';

const DEFAULT_KB_PAN = 15;
const DEFAULT_KB_ZOOM = 0.15;

export function useEvents<
  K1,
  K2,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  touchResponse: 'touchStart' | 'touchHold',
  initNode: D1,
  nodes: D1[],
  links: D2[],
  promotedLinks: PromotedLinkId<K1, D2>[],
  nodeBounds: Map<K1, Bounds>,
  linkIdToDataMap: Map<K2, D2>,
  nodeIdToDataMap: Map<K1, D1>,
  width: number,
  height: number,
  maxZoom: number,
  expanded: Keys<K1>,
  itemIdToParentMap: Map<K1, K1>,
  promotedLinkMap: Map<string, PromotedLinkId<K1, D2>>,
  onItemHover?: (detail: ItemHoverDetail<K1, K2, D1, D2>) => void,
  onItemFocus?: (detail: ItemFocusDetail<K1, K2, D1, D2>) => void,
  kbPanCallback?: (props: {
    dPan?: { dx: number; dy: number };
    nPan?: { cx: number; cy: number };
    center?: boolean;
  }) => void,
  kbZoomCallback?: (props: { dZoom?: number; nZoom?: number }) => void,
  onExpandedChange?: (detail: ExpandedItemDetail<K1>) => void
) {
  const {
    getNextNavigableNode,
    getNextNavigableLink,
    navigateFromNodeToLink,
    navigateFromLinkToNode
  } = useDiagramNavigation<K1, K2, D1, D2>(
    nodeBounds,
    linkIdToDataMap,
    promotedLinkMap,
    nodeIdToDataMap
  );
  const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo<K1, K2, D2>>({
    id: initNode.id,
    type: 'node'
  });
  const [hoveredItemInfo, sethoveredItemInfo] = useState<ItemInfo<K1, K2, D2>>();
  const activeId = useRef<string>();

  const cancelEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;
    let eventConsumed = false;
    // tabbing out of the diagram
    if (key === 'TAB') {
      return;
    }
    // if this a switch from node to link or vice versa
    if (isChangeItemType(event)) {
      let nextItemInfo: ItemInfo<K1, K2, D2> | undefined;
      if (focusedItemInfo.type === 'node') {
        nextItemInfo = navigateFromNodeToLink(
          links,
          promotedLinks,
          event,
          nodeIdToDataMap.get(focusedItemInfo.id)
        );
      } else if (focusedItemInfo.type === 'link') {
        const data = linkIdToDataMap.get(focusedItemInfo.id as K2)!;
        nextItemInfo = navigateFromLinkToNode(data.startNode, data.endNode, event);
      } else {
        // promoted link
        const id = focusedItemInfo.id;
        nextItemInfo = navigateFromLinkToNode(id.startNode, id.endNode, event);
      }
      handleKeyboardInput(nextItemInfo);
      eventConsumed = true;
    } else if (isNavigationEvent(key)) {
      const nextItemInfo = handleKeyboardNavigationEvent(key) as ItemInfo<K1, K2, D2>;
      handleKeyboardInput(nextItemInfo);
      eventConsumed = true;
    } else {
      switch (key) {
        case 'PageUp': {
          kbPanCallback &&
            kbPanCallback({
              dPan: {
                dx: event.shiftKey ? -DEFAULT_KB_PAN : 0,
                dy: event.shiftKey ? 0 : -DEFAULT_KB_PAN
              }
            });
          eventConsumed = true;
          break;
        }
        case 'PageDown': {
          kbPanCallback &&
            kbPanCallback({
              dPan: {
                dx: event.shiftKey ? DEFAULT_KB_PAN : 0,
                dy: event.shiftKey ? 0 : DEFAULT_KB_PAN
              }
            });
          eventConsumed = true;
          break;
        }
        case '+':
        case '=': {
          kbZoomCallback && kbZoomCallback({ dZoom: DEFAULT_KB_ZOOM });
          eventConsumed = true;
          break;
        }
        case '-':
        case '_': {
          kbZoomCallback && kbZoomCallback({ dZoom: -DEFAULT_KB_ZOOM });
          eventConsumed = true;
          break;
        }
        case '0': {
          if (event.ctrlKey && event.altKey) {
            const { centerX, centerY, zoom } = zoomAndCenterItemFromInfo(
              focusedItemInfo,
              linkIdToDataMap,
              nodeBounds,
              width,
              height,
              maxZoom
            );
            kbZoomCallback && kbZoomCallback({ nZoom: zoom });
            kbPanCallback && kbPanCallback({ nPan: { cx: centerX, cy: centerY } });
          } else {
            // zoom to fit
            kbZoomCallback && kbZoomCallback({});
            kbPanCallback && kbPanCallback({ center: true });
          }
          eventConsumed = true;
          break;
        }
        case ' ': {
          if (event.ctrlKey && event.shiftKey) {
            if (focusedItemInfo.type === 'node') {
              const id = focusedItemInfo.id;
              const newExpanded = changeExpanded(id, expanded);
              onExpandedChange && onExpandedChange({ expanded: newExpanded });
            }
          }
          break;
        }
        case '[': {
          if (focusedItemInfo.type === 'node') {
            const child = getChildInfo(focusedItemInfo.id, nodeIdToDataMap);
            if (child) {
              const item = { id: child.id, type: 'node' } as ItemInfo<K1, K2, D2>;
              handleKeyboardInput(item);
            }
          }
          eventConsumed = true;
          break;
        }
        case ']': {
          if (focusedItemInfo.type === 'node') {
            const parentId = itemIdToParentMap.get(focusedItemInfo.id);
            if (parentId) {
              const item = { id: parentId, type: 'node' } as ItemInfo<K1, K2, D2>;
              handleKeyboardInput(item);
            }
          }
          eventConsumed = true;
          break;
        }
      }
    }
    if (eventConsumed) {
      cancelEvent(event);
    }
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        break;
      }
    }
  };

  const blurHandler = () => {
    if (hoveredItemInfo != null || focusedItemInfo.isCurrent) {
      onItemFocus?.(undefined);
    }
    const itemInfo = { ...focusedItemInfo, isCurrent: false, isFocusVisible: false };
    setfocusedItemInfo(itemInfo);
  };

  const onHover = (event: PointerEvent) => {
    const itemInfo = getNavigableInfo<K1, K2, D2>(event.target as HTMLElement, promotedLinkMap);
    if (itemInfo && !isEqualItem(itemInfo, hoveredItemInfo)) {
      sethoveredItemInfo({ ...itemInfo, isCurrent: true });
      activeId.current = getRandomId();
      setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false });
      let detail: ItemHoverDetail<K1, K2, D1, D2>;
      if (itemInfo.type === 'node') {
        detail = {
          id: itemInfo.id,
          type: 'node',
          data: nodeIdToDataMap.get(itemInfo.id as K1)
        };
      } else if (itemInfo.type === 'link') {
        detail = {
          id: itemInfo.id,
          type: 'link',
          data: linkIdToDataMap.get(itemInfo.id as K2)
        };
      } else if (itemInfo.type === 'promotedLink') {
        detail = {
          type: 'promotedLink',
          id: itemInfo.id
        };
      }
      onItemHover?.(detail);
    }
  };

  const onHoverLeave = () => {
    sethoveredItemInfo(undefined);
    activeId.current = undefined;
    onItemHover?.(undefined);
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);

  const pointerUpHandler = (event: PointerEvent) => {
    const itemInfo = getNavigableInfo<K1, K2, D2>(event.target as HTMLElement, promotedLinkMap);
    if (itemInfo.id != null) {
      let buttonId;
      if (itemInfo.type === 'node') {
        buttonId = expCollapseButtonId<K1>(event.target as HTMLElement);
        if (buttonId) {
          const newExpanded = changeExpanded(buttonId, expanded);
          onExpandedChange && onExpandedChange({ expanded: newExpanded });
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }
      setfocusedItemInfo(itemInfo);
      activeId.current = getRandomId();
    }
  };

  function updatefocusedItemInfo(itemInfo: ItemInfo<K1, K2, D2>) {
    if (itemInfo.type === 'node') {
      onItemFocus?.({
        id: itemInfo.id as K1,
        type: 'node',
        data: nodeIdToDataMap.get(itemInfo.id)
      });
    } else if (itemInfo.type === 'link') {
      onItemFocus?.({
        id: itemInfo.id as K2,
        type: 'link',
        data: linkIdToDataMap.get(itemInfo.id)
      });
    } else if (itemInfo.type === 'promotedLink') {
      onItemFocus?.({
        id: itemInfo.id,
        type: 'promotedLink'
      });
    }
    activeId.current = getRandomId();
    setfocusedItemInfo(itemInfo);
  }

  function handleKeyboardInput(item?: ItemInfo<K1, K2, D2>) {
    if (!item) return;
    if (!isEqualItem(item, focusedItemInfo)) {
      item.isCurrent = true;
      item.isFocusVisible = true;
      if (hoveredItemInfo) {
        sethoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      updatefocusedItemInfo(item);
    }
  }

  function isNavigationEvent(key: string) {
    switch (key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'ArrowRight':
      case 'ArrowLeft': {
        return true;
      }
    }
    return false;
  }

  function isChangeItemType(event: KeyboardEvent) {
    const key = event.code;
    // check if switching from node to link
    // equivalent of ≤ and ≥
    if (focusedItemInfo.type === 'node' && event.altKey && (key === 'Period' || key === 'Comma')) {
      return true;
      // check if switching from link to node
    } else if (
      !(focusedItemInfo.type === 'node') &&
      (key === 'ArrowLeft' || key === 'ArrowRight')
    ) {
      return true;
    }
    return false;
  }

  function handleKeyboardNavigationEvent(key: string) {
    let direction: NavDirection = 'down';
    switch (key) {
      case 'ArrowDown': {
        direction = 'down';
        break;
      }
      case 'ArrowUp': {
        direction = 'up';
        break;
      }
      case 'ArrowRight': {
        direction = 'right';
        break;
      }
      case 'ArrowLeft': {
        direction = 'left';
        break;
      }
    }
    if (focusedItemInfo.type === 'node') {
      const parent = itemIdToParentMap.get(focusedItemInfo.id as K1);
      const navigableNodes =
        parent && nodeIdToDataMap.get(parent) ? nodeIdToDataMap.get(parent)!.nodes! : nodes;
      const itemDetail = getNextNavigableNode(
        direction,
        true,
        nodeIdToDataMap.get(focusedItemInfo.id as K1),
        navigableNodes
      );
      return itemDetail ? { id: itemDetail.id, type: 'node' } : focusedItemInfo;
    } else {
      const linkDetail = getLinkDetailFromInfo<K1, K2, D2>(focusedItemInfo, links, promotedLinks);
      const itemDetail = getNextNavigableLink(direction, nodes, linkDetail, links, promotedLinks);
      if (itemDetail && isPromoted(itemDetail)) return { id: itemDetail, type: 'promotedLink' };
      else if (itemDetail && !isPromoted(itemDetail))
        return { id: (itemDetail as D2).id, type: 'link' };
      else return { id: focusedItemInfo.id, type: focusedItemInfo.type };
    }
  }
  const onContextMenuDismissed = (gesture: gestureTypes) => {
    if (gesture === 'keyboard') {
      updatefocusedItemInfo({
        ...focusedItemInfo,
        isFocusVisible: true,
        isCurrent: true
      });
    }
  };

  const onFocus = () => {
    activeId.current = getRandomId();
  }
  return {
    focusedItemInfo,
    hoveredItemInfo,
    activeId: activeId.current,
    onContextMenuDismissed,
    eventsProps: mergeProps(hoverHandlers, {
      onKeyUp: keyUpHandler,
      onKeyDown: keyDownHandler,
      onBlur: blurHandler,
      onPointerUp: pointerUpHandler,
      onFocus
    })
  };
}
